import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import Icon from '../../components/AppIcon';
// import Input from './Input';
import AppImage from '../../components/AppImage'; // Import AppImage

const Header = ({
  variant = 'default',
  onMenuToggle,
  className = '',
  ...props
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const companyMenuRef = useRef(null);
const userMenuRef = useRef(null);
const mobileMenuButtonRef = useRef(null);

useEffect(() => {
  const handleClickOutside = (event) => {
    // Close user menu if click is outside user menu button or menu
    if (
      isUserMenuOpen &&
      userMenuRef.current &&
      !userMenuRef.current.contains(event.target)
    ) {
      setIsUserMenuOpen(false);
    }

    // Close company menu if click is outside company menu button or menu
    if (
      isCompanyMenuOpen &&
      companyMenuRef.current &&
      !companyMenuRef.current.contains(event.target)
    ) {
      setIsCompanyMenuOpen(false);
    }

    // Close mobile menu if click is outside mobile menu button and menu
    if (
      isMobileMenuOpen &&
      mobileMenuButtonRef.current &&
      !mobileMenuButtonRef.current.contains(event.target) &&
      !event.target.closest('.mobile-menu-container')
    ) {
      setIsMobileMenuOpen(false);
      if (onMenuToggle) onMenuToggle(false);
    }
  };

  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, [isUserMenuOpen, isCompanyMenuOpen, isMobileMenuOpen, onMenuToggle]);


  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Changed 'Recommendations' to 'For You' and moved to the beginning
  const navigationItems = [
    { name: 'For You', path: '/recommendations', icon: 'Star' },
    { name: 'Products', path: '/products', icon: 'ShoppingBag' },
    { name: 'Account', path: '/user-account-page', icon: 'User' },
    { name: 'API Docs', path: '/api-documentation-page', icon: 'FileText' },
    { name: 'Skin Passport', path: '/skin-passport', icon: 'CreditCard' },
    { name: 'For Business', path: '/for-business', icon: 'Briefcase' },
    { name: 'Prices', path: '/prices', icon: 'DollarSign' }
  ];

  const navigationItemsMobile = [
    { name: 'For You', path: '/recommendations', icon: 'Star' },
    { name: 'Products', path: '/products', icon: 'ShoppingBag' },
    { name: 'Account', path: '/user-account-page', icon: 'User' },
    { name: 'API Docs', path: '/api-documentation-page', icon: 'FileText' },
    { name: 'Skin Passport', path: '/skin-passport', icon: 'CreditCard' },
    { name: 'For Business', path: '/for-business', icon: 'Briefcase' },
    { name: 'Prices', path: '/prices', icon: 'DollarSign' },
    { name: 'About', path: '/about' },
    { name: 'Brand', path: '/brand' },
    { name: 'Our Team', path: '/our-team' },
    { name: 'Jobs', path: '/jobs' }
  ];

  const companyDropdownItems = [
    { name: 'About', path: '/about' },
    { name: 'Brand', path: '/brand' },
    { name: 'Our Team', path: '/our-team' },
    { name: 'Jobs', path: '/jobs' },
  ];

  const handleCompanyDropdownClick = useCallback((event) => {
    event.preventDefault();
    setIsCompanyMenuOpen((prev) => !prev);
  }, []);

  const handleCompanyLinkClick = (path) => {
    setIsCompanyMenuOpen(false);
    navigate(path);
  };

  const handleLinkClick = (path) => {
    navigate(path);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (onMenuToggle) {
      onMenuToggle(!isMobileMenuOpen);
    }
  };

  const Logo = () => (
    <Link to="/" className="flex items-center">
      <div className="flex items-center text-primary-light">
        <AppImage
          src="https://cosmelytix-brand-book-public.s3.us-east-1.amazonaws.com/brand_materials/cosmelytix_logo.svg"
          smallSrc="https://cosmelytix-brand-book-public.s3.us-east-1.amazonaws.com/brand_materials/cosmelytix_mini_logo.svg"
          alt="Cosmelytix Logo"
          className="h-12 w-auto"
        />
      </div>
    </Link>
  );

  if (variant === 'compact') {
    return (
      <header
        className={`bg-white border-b border-border py-2 px-4 ${className}`}
        {...props}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div
              className="mr-2 md:hidden"
              style={{
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <button
                type="button"
                className="text-[#E9A969] hover:text-primary-light"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
                style={{ width: 40, height: 40 }}
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} color='#E9A969' stroke='#E9A969' />
              </button>
            </div>

            <Logo />
          </div>

          <div className="flex items-center space-x-2">
            <div className="user-menu-container relative">
              <button
                type="button"
                className="p-2 text-secondary hover:text-primary rounded-full hover:bg-surface"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-label="User menu"
                aria-expanded={isUserMenuOpen}
              >
                <Icon name="User" size={18} />
              </button>

              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-md border bg-['#A89B8D'] border-border z-10 animate-scale-up">
                  <div className="py-1">
                    <Link
                      to="/user-account-page"
                      className="block px-4 py-2 text-secondary hover:bg-surface"
                    >
                      My Account
                    </Link>
                    <Link
                      to="/products"
                      className="block px-4 py-2 text-text-secondary hover:bg-surface"
                    >
                      Products
                    </Link>
                    <Link
                      to="/skin-passport"
                      className="block px-4 py-2 text-text-secondary hover:bg-surface"
                    >
                      Skin Passport
                    </Link>
                    <Link
                      to="/prices"
                      className="block px-4 py-2 text-white hover:bg-surface"
                    >
                      Prices
                    </Link>
                    <div className="border-t border-border my-1"></div>
                    <button onClick={handleSignOut} className="w-full text-left px-4 py-2 text-error hover:bg-surface">
                      <Icon name="LogOut" size={16} className="inline-block mr-2" color="#5F40A1" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className={`bg-['#A89B8D'] border-b border-border ${className}`} {...props}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <button
              type="button"
              className="mr-2 md:hidden text-text-secondary hover:text-primary"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>

            <Logo />

            <nav className="hidden md:ml-8 md:flex md:space-x-6">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center px-1 py-2 text-sm font-medium border-b-2 ${
                    location.pathname === item.path
                      ? 'border-primary text-primary'
                      : 'border-transparent text-primary-light hover:text-primary hover:border-primary'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div
                className="relative"
                ref={companyMenuRef}
                style={{ display: 'flex' }}
              >
                <button
                  onClick={handleCompanyDropdownClick}
                  className="flex items-center px-1 py-2 text-sm font-medium border-b-2 border-transparent text-primary-light hover:text-primary hover:border-primary"
                  aria-haspopup="true"
                  aria-expanded={isCompanyMenuOpen}
                >
                  Company
                  <Icon
                    name="ChevronDown"
                    size={16}
                    className={`ml-1.5 transition-transform ${
                      isCompanyMenuOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {isCompanyMenuOpen && (
                  <div
                    className="absolute left-0 mt-2 w-48 bg-['#A89B8D'] rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10 animate-scale-up"
                    style={{ top: '105%', transformOrigin: 'top left', backgroundColor: '#A89B8D' }}
                  >
                    <div
                      className="py-1"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="options-menu"
                    >
                      {companyDropdownItems.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => handleCompanyLinkClick(item.path)}
                          className="block w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-surface"
                          role="menuitem"
                        >
                          {item.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </nav>
          </div>

          <div className="flex items-center">
            <div className="ml-4 relative user-menu-container">
              <button
                type="button"
                className="flex items-center text-text-secondary hover:text-primary-light"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-expanded={isUserMenuOpen}
              >
                <span className="hidden md:block mr-2 text-sm font-medium uppercase text-[#E9A969] hover:text-[#CFC7DF]">
                  Profile
                </span>
                <AppImage
                  src="/assets/images/icons/pfp.svg"
                  alt="Profile Icon"
                  className="h-8 w-8 rounded-full object-cover"
                />
              </button>

              {isUserMenuOpen && ( //here
                <div className="absolute right-0 mt-2 w-48 bg-[#A89B8D] rounded-md shadow-md z-10 animate-scale-up">
                  <div className="py-1">
                    <Link
                      to="/user-account-page"
                      className="block px-4 py-2 text-sm  text-text-secondary hover:bg-surface"
                    >
                      My Account
                    </Link>
                    <Link
                      to="/products"
                      className="block px-4 py-2 text-sm text-text-secondary hover:bg-surface"
                    >
                      Products
                    </Link>
                    <Link
                      to="/api-documentation-page"
                      className="block px-4 py-2 text-sm text-text-secondary hover:bg-surface"
                    >
                      API Documentation
                    </Link>
                    <Link
                      to="/skin-passport"
                      className="block px-4 py-2 text-sm text-text-secondary hover:bg-surface"
                    >
                      Skin Passport
                    </Link>
                    <Link
                      to="/prices"
                      className="block px-4 py-2 text-sm text-text-secondary hover:bg-surface"
                    >
                      Prices
                    </Link>
                    <div className="border-t my-1" style={{'border-color':'#CFC7DF'}}></div>
                    <button onClick={handleSignOut} className="w-full text-left px-4 py-2 text-sm text-[#5F40A1] hover:bg-surface">
                      <Icon name="LogOut" size={16} className="inline-block mr-2" />
                      Sign Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

               



{/* Mobile menu */}
{isMobileMenuOpen && (
  <div className="md:hidden border-t border-border animate-fade-in">
    <div className="pt-2 pb-3 space-y-1">
      {/* Use navigationItemsMobile instead of navigationItems */}
      {navigationItemsMobile.map((item) => (
        <button
          key={item.name}
          onClick={() => handleLinkClick(item.path)} // Use handleLinkClick
          className={`flex items-center w-full text-left px-4 py-2 text-base font-medium ${
            location.pathname === item.path
              ? 'bg-[#5F40A1] bg-opacity-50 text-primary-light'
              : 'text-primary hover:bg-[#5F40A1]'
          }`}
        >
          {item.icon && <Icon name={item.icon} size={20} className="mr-3" />}
          {item.name}
        </button>
      ))}
    </div>
  </div>
)}





      
    </header>
  );
};

export default Header;